import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Pencil, Trash2, Search, Users, CreditCard, AlertCircle, Eye, Receipt, Wallet, Clock, MapPin, Calendar, Phone, Mail, DollarSign, FileText, CheckCircle, AlertTriangle, ArrowRight, ClipboardList, CheckCircle2, ChevronDown, Building2, Anchor, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EnhancedInput } from '@/components/ui/enhanced-input';
import { LoadingTable, LoadingCard } from '@/components/ui/loading';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatCurrency } from '@/lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer, getCustomerInvoices, getInvoices, getCompanySettings } from '@/lib/api';
import type { Customer, Invoice, CompanySettings } from '@/types/firebase';
import { useAuth } from '@/components/AuthProvider';
import { useEffectiveCompanyId } from '@/hooks/useEffectiveCompanyId';
import { showNotification } from '@/lib/generalNotifications';
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { CustomerProfileModal } from '@/components/CustomerProfileModal';
import { CustomerFormFields, type CustomerFormData as SharedCustomerFormData } from '@/components/forms/CustomerFormFields';

// Import the country and state data
import { countries } from '@/data/countries';

// Define US states directly since the module is missing
interface USState {
  name: string;
  code: string;
}

// US states data
const usStates: USState[] = [
  { name: "Alabama", code: "AL" },
  { name: "Alaska", code: "AK" },
  { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" },
  { name: "California", code: "CA" },
  { name: "Colorado", code: "CO" },
  { name: "Connecticut", code: "CT" },
  { name: "Delaware", code: "DE" },
  { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" },
  { name: "Hawaii", code: "HI" },
  { name: "Idaho", code: "ID" },
  { name: "Illinois", code: "IL" },
  { name: "Indiana", code: "IN" },
  { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" },
  { name: "Kentucky", code: "KY" },
  { name: "Louisiana", code: "LA" },
  { name: "Maine", code: "ME" },
  { name: "Maryland", code: "MD" },
  { name: "Massachusetts", code: "MA" },
  { name: "Michigan", code: "MI" },
  { name: "Minnesota", code: "MN" },
  { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" },
  { name: "Montana", code: "MT" },
  { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" },
  { name: "New Hampshire", code: "NH" },
  { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" },
  { name: "New York", code: "NY" },
  { name: "North Carolina", code: "NC" },
  { name: "North Dakota", code: "ND" },
  { name: "Ohio", code: "OH" },
  { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" },
  { name: "Pennsylvania", code: "PA" },
  { name: "Rhode Island", code: "RI" },
  { name: "South Carolina", code: "SC" },
  { name: "South Dakota", code: "SD" },
  { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" },
  { name: "Utah", code: "UT" },
  { name: "Vermont", code: "VT" },
  { name: "Virginia", code: "VA" },
  { name: "Washington", code: "WA" },
  { name: "West Virginia", code: "WV" },
  { name: "Wisconsin", code: "WI" },
  { name: "Wyoming", code: "WY" },
  { name: "District of Columbia", code: "DC" }
];

interface CustomerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName?: string;
  street: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  notes: string;
  // Optional fields for cruise ship industry
  cabinNumber?: string;
  hostName?: string;
}

const initialFormData: CustomerFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  street: '',
  city: '',
  state: '',
  country: 'United States',
  zip: '',
  notes: '',
  cabinNumber: '',
  hostName: ''
};

// Define an extended Invoice type that includes metadata
interface InvoiceWithMetadata extends Invoice {
  metadata?: {
    isRecurring?: boolean;
    isInstallment?: boolean;
    wasRefunded?: boolean;
    refundDate?: string;
    statusOverride?: string;
    [key: string]: any;
  };
}

// Update the formatDate function to handle Timestamp
const formatDate = (date: Date | Timestamp | null) => {
  if (!date) return '';
  if (date instanceof Timestamp) {
    return date.toDate().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

// Phone number formatter
const formatPhoneNumber = (input: string): string => {
  // Strip all non-numeric characters
  const cleaned = input.replace(/\D/g, '');
  
  // Check if it's a US number (starts with 1 or has 10 digits)
  if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
    // Format as US number: +1 (XXX) XXX-XXXX
    const countryCode = cleaned.length === 11 ? cleaned.substring(0, 1) : '1';
    const areaCode = cleaned.substring(cleaned.length - 10, cleaned.length - 7);
    const firstPart = cleaned.substring(cleaned.length - 7, cleaned.length - 4);
    const lastPart = cleaned.substring(cleaned.length - 4);
    
    return `+${countryCode} (${areaCode}) ${firstPart}-${lastPart}`;
  }
  
  // For non-US numbers or incomplete numbers, just return the cleaned version
  return cleaned;
};

// Replace the GradientBorder component with this
const StylishSelectContent = (props: React.ComponentProps<typeof SelectContent>) => {
  return (
    <SelectContent
      {...props}
      className={cn(
        "max-h-[200px] overflow-y-auto rounded-lg",
        "bg-white shadow-[0_0_15px_rgba(59,130,246,0.5)]",
        "border-[3px] border-transparent",
        "bg-gradient-to-br from-violet-500 via-transparent to-blue-500 bg-origin-border",
        props.className
      )}
      position="popper"
      sideOffset={5}
    />
  );
};

// Add a custom styled trigger for Select elements
const StylishSelectTrigger = (props: React.ComponentProps<typeof SelectTrigger>) => {
  return (
    <SelectTrigger
      {...props}
      className={cn(
        "h-10 w-full transition-all duration-200",
        "hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-300/30", 
        props.className
      )}
    />
  );
};

// Simple Badge component since we don't have the actual one
const Badge = ({ children, className }: { children: React.ReactNode, className?: string }) => {
  return (
    <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", className)}>
      {children}
    </span>
  );
};

// Move the function outside the component to make it available to all components
const getInvoiceStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'overdue':
      return 'bg-red-100 text-red-800';
    case 'sent':
      return 'bg-blue-100 text-blue-800';
    case 'draft':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Helper function to determine customer status for display
const getCustomerStatusDisplay = (customer: Customer) => {
  // Use paymentStatus as a fallback, but this should be updated based on actual invoices
  if (customer.paymentStatus === 'overdue') {
    return 'Past due';
  } else {
    return 'Current';
  }
};

const tableHeaderLabelClass = 'text-[11px] font-semibold uppercase tracking-wide text-slate-600';

// Helper function to get customer status class
const getCustomerStatusClass = (customer: Customer) => {
  // Use paymentStatus as a fallback, but this should be updated based on actual invoices
  if (customer.paymentStatus === 'overdue') {
    return 'bg-red-100 text-red-800';
  } else {
    return 'bg-green-100 text-green-800';
  }
};

export default function Customers() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isCustomerProfileModalOpen, setIsCustomerProfileModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState<CustomerFormData>(initialFormData);
  const [showStateDropdown, setShowStateDropdown] = useState(formData.country === 'United States');
  const [isCustomerInfoExpanded, setIsCustomerInfoExpanded] = useState(false);
  const [isInvoiceHistoryExpanded, setIsInvoiceHistoryExpanded] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { effectiveCompanyId } = useEffectiveCompanyId();
  const [activeCustomerFilter, setActiveCustomerFilter] = useState<'all' | 'current' | 'past-due' | 'recurring'>('all');
  const navigate = useNavigate();

  // Fetch customers
  const { data: customers = [], isLoading } = useQuery({
    queryKey: ['customers', effectiveCompanyId],
    queryFn: () => {
      if (!effectiveCompanyId) {
        console.error('[Customers.tsx] ERROR: Attempting to fetch without companyId!');
        return [];
      }
      return getCustomers(effectiveCompanyId);
    },
    enabled: !!effectiveCompanyId
  });

  // Fetch all invoices to calculate customer statuses
  const { data: allInvoices = [], isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['invoices', effectiveCompanyId],
    queryFn: () => {
      if (!effectiveCompanyId) {
        console.error('[Customers.tsx] ERROR: Attempting to fetch invoices without companyId!');
        return [];
      }
      return getInvoices(effectiveCompanyId);
    },
    enabled: !!effectiveCompanyId
  });

  // Fetch customer invoices when viewing
  const { data: customerInvoices = [], isLoading: isLoadingCustomerInvoices } = useQuery({
    queryKey: ['customerInvoices', selectedCustomer?.id, effectiveCompanyId],
    queryFn: async () => {
      if (!selectedCustomer?.id || !effectiveCompanyId) return [];
      console.log('Fetching invoices for customer:', selectedCustomer.id);
      try {
        const invoices = await getCustomerInvoices(selectedCustomer.id, effectiveCompanyId);
        console.log('Fetched invoices:', invoices);
        return invoices || [];
      } catch (error) {
        console.error('Error fetching customer invoices:', error);
        return [];
      }
    },
    enabled: !!selectedCustomer?.id && !!effectiveCompanyId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Calculate customer metrics
  const customerMetrics = useMemo(() => {
    const totalCustomers = customers.length;
    
    // Create a map of customers with past due invoices
    const customersWithPastDueInvoices = new Set<string>();
    // Create a map of customers with recurring invoices
    const customersWithRecurringInvoices = new Set<string>();

    // Process all invoices to identify customers with past due or recurring invoices
    allInvoices.forEach((invoice) => {
      const invoiceWithMetadata = invoice as InvoiceWithMetadata;
      
      if (invoice.status === 'overdue' && invoice.customerId) {
        customersWithPastDueInvoices.add(invoice.customerId);
      }
      if (invoice.invoiceType === 'recurring' || 
          (invoice.recurringDetails && invoice.recurringDetails.isActive) ||
          (invoiceWithMetadata.metadata && invoiceWithMetadata.metadata.isRecurring)) {
        customersWithRecurringInvoices.add(invoice.customerId);
      }
    });

    return {
      total: totalCustomers,
      current: totalCustomers - customersWithPastDueInvoices.size,
      pastDue: customersWithPastDueInvoices.size,
      recurring: customersWithRecurringInvoices.size
    };
  }, [customers, allInvoices]);

  // Filter customers based on active filter and search term
  const filteredCustomers = useMemo(() => {
    // Create sets for quick lookups
    const pastDueCustomerIds = new Set<string>();
    const recurringCustomerIds = new Set<string>();

    // Process all invoices to identify customer statuses
    allInvoices.forEach((invoice) => {
      const invoiceWithMetadata = invoice as InvoiceWithMetadata;

      if (invoice.status === 'overdue' && invoice.customerId) {
        pastDueCustomerIds.add(invoice.customerId);
      }
      if (invoice.invoiceType === 'recurring' ||
          (invoice.recurringDetails && invoice.recurringDetails.isActive) ||
          (invoiceWithMetadata.metadata && invoiceWithMetadata.metadata.isRecurring)) {
        recurringCustomerIds.add(invoice.customerId);
      }
    });

    // Filter customers based on active filter and search term
    return customers.filter(customer => {
      // First apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          (customer.name?.toLowerCase().includes(search)) ||
          (customer.companyName?.toLowerCase().includes(search)) ||
          (customer.email?.toLowerCase().includes(search)) ||
          (customer.phone?.toLowerCase().includes(search));

        if (!matchesSearch) {
          return false;
        }
      }

      // Then apply status filter
      if (activeCustomerFilter === 'all') {
        return true;
      }

      const hasPastDueInvoices = pastDueCustomerIds.has(customer.id);
      const hasRecurringInvoices = recurringCustomerIds.has(customer.id);

      switch (activeCustomerFilter) {
        case 'current':
          return !hasPastDueInvoices;
        case 'past-due':
          return hasPastDueInvoices;
        case 'recurring':
          return hasRecurringInvoices;
        default:
          return true;
      }
    });
  }, [customers, allInvoices, activeCustomerFilter, searchTerm]);

  // Mutations
  const createCustomerMutation = useMutation({
    mutationFn: (customer: Omit<Customer, 'id' | 'createdAt'>) => 
      createCustomer(customer, effectiveCompanyId || `company_${user?.uid}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', effectiveCompanyId] });
      setIsAddDialogOpen(false);
      setFormData(initialFormData);
      showNotification({
        title: "Success",
        message: "Customer added successfully",
        type: "success"
      });
    },
    onError: (error: Error) => {
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to add customer",
        type: "error"
      });
    }
  });

  const updateCustomerMutation = useMutation({
    mutationFn: ({ id, customer }: { id: string; customer: Partial<Customer> }) => 
      updateCustomer(effectiveCompanyId || `company_${user?.uid}`, id, customer),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', effectiveCompanyId] });
      setIsEditDialogOpen(false);
      setFormData(initialFormData);
      showNotification({
        title: "Success",
        message: "Customer updated successfully",
        type: "success"
      });
    }
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: (id: string) => deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', effectiveCompanyId] });
      showNotification({
        title: "Success",
        message: "Customer deleted successfully",
        type: "success"
      });
    },
    onError: (error: Error) => {
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete customer",
        type: "error"
      });
    }
  });

  // Effect to toggle state dropdown when country changes
  useEffect(() => {
    setShowStateDropdown(formData.country === 'United States');
  }, [formData.country]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle phone blur to format US numbers
  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (formData.country === 'United States') {
      const formattedPhone = formatPhoneNumber(e.target.value);
      setFormData(prev => ({
        ...prev,
        phone: formattedPhone
      }));
    }
  };

  // Handlers for CustomerFormFields component
  const handleFieldChange = (field: keyof SharedCustomerFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (address: { street: string; city: string; state: string; zipCode: string }) => {
    setFormData(prev => ({
      ...prev,
      street: address.street,
      city: address.city,
      state: address.state,
      zip: address.zipCode
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Basic validation
      const requiredFields: (keyof CustomerFormData)[] = [
        'firstName',
        'lastName',
        'email',
        'phone',
        'street',
        'city',
        'state',
        'country',
        'zip'
      ];

      const missingFields = requiredFields.filter(field => !formData[field]);
      if (missingFields.length > 0) {
        throw new Error(`Required fields missing: ${missingFields.join(', ')}`);
      }

      // Phone number validation - more permissive to allow international formats
      const phoneRegex = /^\+?[\d\s-()]{10,}$/;
      if (!phoneRegex.test(formData.phone)) {
        throw new Error('Invalid phone number format');
      }

      if (!user) {
        throw new Error('You must be logged in to create a customer');
      }

      await createCustomerMutation.mutateAsync({
        ...formData,
        status: 'active',
        paymentStatus: 'pending',
        companyId: effectiveCompanyId || `company_${user?.uid}`,
        updatedAt: serverTimestamp() as unknown as Timestamp
      });
    } catch (error) {
      console.error(error);
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to add customer",
        type: "error"
      });
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Basic validation
      const requiredFields: (keyof CustomerFormData)[] = [
        'firstName',
        'lastName',
        'email',
        'phone',
        'street',
        'city',
        'state',
        'country',
        'zip'
      ];

      const missingFields = requiredFields.filter(field => !formData[field]);
      if (missingFields.length > 0) {
        throw new Error(`Required fields missing: ${missingFields.join(', ')}`);
      }

      // Phone number validation - more permissive to allow international formats
      const phoneRegex = /^\+?[\d\s-()]{10,}$/;
      if (!phoneRegex.test(formData.phone)) {
        throw new Error('Invalid phone number format');
      }

      if (!user) {
        throw new Error('You must be logged in to update a customer');
      }

      if (!selectedCustomer) {
        throw new Error('No customer selected for update');
      }

      await updateCustomerMutation.mutateAsync({
        id: selectedCustomer.id,
        customer: {
          ...formData,
          status: selectedCustomer.status || 'active',
          paymentStatus: selectedCustomer.paymentStatus || 'pending',
          companyId: effectiveCompanyId || `company_${user?.uid}`,
          updatedAt: serverTimestamp() as unknown as Timestamp
        }
      });
    } catch (error) {
      console.error(error);
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update customer",
        type: "error"
      });
    }
  };

  const handleDeleteCustomer = async (id: string) => {
    setCustomerToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (customerToDelete) {
      try {
        await deleteCustomerMutation.mutateAsync(customerToDelete);
        setIsDeleteDialogOpen(false);
        setCustomerToDelete(null);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleViewInvoices = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsCustomerProfileModalOpen(true);
  };

  const getInvoiceMetrics = (invoices: typeof customerInvoices) => {
    return {
      totalAmount: invoices.reduce((sum, inv) => sum + inv.total, 0),
      paidAmount: invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0),
      get outstandingAmount() {
        return this.totalAmount - this.paidAmount;
      },
      totalInvoices: invoices.length,
      paidInvoices: invoices.filter(inv => inv.status === 'paid').length,
      pendingInvoices: invoices.filter(inv => ['draft', 'sent', 'overdue'].includes(inv.status)).length
    };
  };

  const metrics = selectedCustomer ? getInvoiceMetrics(customerInvoices) : null;

  // Handle opening customer details
  const handleOpenCustomerDetails = (customer: Customer, e: React.MouseEvent) => {
    // Prevent opening if clicking on action buttons or view button
    if ((e.target as HTMLElement).closest('.customer-action-buttons')) {
      return;
    }
    
    console.log('Opening details for customer:', customer.id, customer.firstName, customer.lastName);
    setSelectedCustomer(customer);
    setIsDetailsDialogOpen(true);
    
    // Invalidate the customer invoices query to ensure fresh data
    queryClient.invalidateQueries({ queryKey: ['customerInvoices', customer.id] });
  };

  // Modify prepareInvoiceData to be more robust
  const prepareInvoiceData = () => {
    console.log('Preparing invoice data, count:', customerInvoices?.length || 0);
    
    if (!customerInvoices || customerInvoices.length === 0) {
      return {
        paid: [],
        pending: [],
        overdue: []
      };
    }

    return {
      paid: customerInvoices.filter(inv => inv.status === 'paid'),
      pending: customerInvoices.filter(inv => ['sent', 'draft'].includes(inv.status || '')),
      overdue: customerInvoices.filter(inv => inv.status === 'overdue')
    };
  };

  // Add filter handling for invoice cards
  const handleFilterClick = (filter: 'all' | 'current' | 'past-due' | 'recurring') => {
    setActiveCustomerFilter(filter);
    console.log('Set filter to:', filter);
  };

  // Get filtered invoices based on active filter
  const getFilteredInvoices = () => {
    if (!customerInvoices) {
      return [];
    }

    switch (activeCustomerFilter) {
      case 'current':
        return customerInvoices.filter(inv => inv.status === 'paid');
      case 'past-due':
        return customerInvoices.filter(inv => inv.status === 'overdue');
      case 'recurring':
        return customerInvoices.filter(inv => ['sent', 'draft'].includes(inv.status || ''));
      default:
        return customerInvoices;
    }
  };

  // Add a function to categorize overdue invoices by age
  const getOverdueAgingBreakdown = () => {
    if (!customerInvoices || customerInvoices.length === 0) {
      return {
        '0-30': [],
        '30-60': [],
        '60-90': [],
        '90+': []
      };
    }

    const overdueInvoices = customerInvoices.filter(inv => inv.status === 'overdue');
    const now = new Date();
    
    return {
      '0-30': overdueInvoices.filter(inv => {
        const dueDate = new Date(inv.dueDate || inv.date || inv.createdAt);
        const daysPastDue = Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
        return daysPastDue >= 0 && daysPastDue < 30;
      }),
      '30-60': overdueInvoices.filter(inv => {
        const dueDate = new Date(inv.dueDate || inv.date || inv.createdAt);
        const daysPastDue = Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
        return daysPastDue >= 30 && daysPastDue < 60;
      }),
      '60-90': overdueInvoices.filter(inv => {
        const dueDate = new Date(inv.dueDate || inv.date || inv.createdAt);
        const daysPastDue = Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
        return daysPastDue >= 60 && daysPastDue < 90;
      }),
      '90+': overdueInvoices.filter(inv => {
        const dueDate = new Date(inv.dueDate || inv.date || inv.createdAt);
        const daysPastDue = Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
        return daysPastDue >= 90;
      })
    };
  };

  // Calculate total amount for each aging bucket
  const getAgingAmounts = () => {
    const aging = getOverdueAgingBreakdown();
    return {
      '0-30': aging['0-30'].reduce((sum, inv) => sum + (inv.total || 0), 0),
      '30-60': aging['30-60'].reduce((sum, inv) => sum + (inv.total || 0), 0),
      '60-90': aging['60-90'].reduce((sum, inv) => sum + (inv.total || 0), 0),
      '90+': aging['90+'].reduce((sum, inv) => sum + (inv.total || 0), 0)
    };
  };

  // Replace the useCompanySettings query with proper syntax
  const companySettingsQuery = useQuery({
    queryKey: ['companySettings', effectiveCompanyId],
    queryFn: async () => {
      if (!effectiveCompanyId) return null;
      return getCompanySettings(effectiveCompanyId);
    },
    enabled: !!effectiveCompanyId
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <p className="text-gray-500">Loading customer data...</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[90rem] py-8 space-y-8">
      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`p-4 cursor-pointer transition ${activeCustomerFilter === 'all' ? 'bg-blue-50 border-blue-200' : ''}`}
          onClick={() => setActiveCustomerFilter('all')}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Customers</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">{customerMetrics.total}</h3>
            </div>
            <div className="p-2 rounded-full bg-blue-100 text-blue-700">
              <Users className="h-5 w-5" />
            </div>
          </div>
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer transition ${activeCustomerFilter === 'current' ? 'bg-green-50 border-green-200' : ''}`}
          onClick={() => setActiveCustomerFilter('current')}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Current</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">{customerMetrics.current}</h3>
            </div>
            <div className="p-2 rounded-full bg-green-100 text-green-700">
              <CheckCircle className="h-5 w-5" />
            </div>
          </div>
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer transition ${activeCustomerFilter === 'past-due' ? 'bg-red-50 border-red-200' : ''}`}
          onClick={() => setActiveCustomerFilter('past-due')}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Past-Due</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">{customerMetrics.pastDue}</h3>
            </div>
            <div className="p-2 rounded-full bg-red-100 text-red-700">
              <AlertCircle className="h-5 w-5" />
            </div>
          </div>
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer transition ${activeCustomerFilter === 'recurring' ? 'bg-purple-50 border-purple-200' : ''}`}
          onClick={() => setActiveCustomerFilter('recurring')}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Recurring</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">{customerMetrics.recurring}</h3>
            </div>
            <div className="p-2 rounded-full bg-purple-100 text-purple-700">
              <Clock className="h-5 w-5" />
            </div>
          </div>
        </Card>
      </div>
      
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
        {/* Search Bar */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search customers..."
            className="pl-10 w-full md:max-w-xs"
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setIsAddDialogOpen(true)} className="flex items-center">
            <Plus className="mr-2 h-4 w-4" />
            Add Customer
          </Button>
          {activeCustomerFilter !== 'all' && (
            <Badge 
              className={`px-3 py-1 ${activeCustomerFilter === 'current' ? 'bg-green-100 text-green-800' : 
                activeCustomerFilter === 'past-due' ? 'bg-red-100 text-red-800' : 
                'bg-purple-100 text-purple-800'}`}
            >
              {activeCustomerFilter === 'current' ? 'Current' : 
               activeCustomerFilter === 'past-due' ? 'Past-Due' : 'Recurring'} 
              <span 
                className="ml-1 cursor-pointer" 
                onClick={() => setActiveCustomerFilter('all')}
              >
                ×
              </span>
            </Badge>
          )}
        </div>
      </div>
      
      {/* Customer Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/90">
              <tr className="border-b">
                <th className="w-[8%]"></th>
                <th className={cn('px-2 py-3 text-left w-[25%]', tableHeaderLabelClass)}>Company Name</th>
                <th className={cn('px-2 py-3 text-left w-[15%]', tableHeaderLabelClass)}>Customer Name</th>
                <th className={cn('px-2 py-3 text-left w-[18%]', tableHeaderLabelClass)}>Email</th>
                <th className={cn('px-2 py-3 text-left w-[15%]', tableHeaderLabelClass)}>Phone</th>
                <th className={cn('px-2 py-3 text-left w-[9%]', tableHeaderLabelClass)}>Status</th>
                <th className={cn('pl-1 pr-2 py-3 text-right w-[10%]', tableHeaderLabelClass)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr 
                  key={customer.id} 
                  className="border-b cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={(e) => handleOpenCustomerDetails(customer, e)}
                >
                  <td className="pl-4 pr-0 py-4 w-[8%]">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">
                        {`${customer.firstName.charAt(0)}${customer.lastName.charAt(0)}`}
                      </span>
                    </div>
                  </td>
                  <td className="pl-1 pr-2 py-4 w-[25%]">
                    <div className="font-medium truncate">
                      {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                    </div>
                  </td>
                  <td className="px-2 py-4 w-[15%]">
                    <div className="text-sm text-gray-500 truncate">
                      {customer.companyName ? `${customer.firstName} ${customer.lastName}` : '-'}
                    </div>
                  </td>
                  <td className="px-2 py-4 text-sm truncate w-[18%]">{customer.email}</td>
                  <td className="px-2 py-4 text-sm w-[15%]">{customer.phone}</td>
                  <td className="px-2 py-4 w-[9%]">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getCustomerStatusClass(customer)}`}>
                      {getCustomerStatusDisplay(customer)}
                    </span>
                  </td>
                  <td className="pl-1 pr-3 py-4 text-right w-[10%]">
                    <div className="flex items-center justify-end space-x-1 customer-action-buttons">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewInvoices(customer);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCustomer(customer);
                          setFormData({
                            firstName: customer.firstName,
                            lastName: customer.lastName,
                            email: customer.email,
                            phone: customer.phone,
                            companyName: customer.companyName || '',
                            street: customer.street,
                            city: customer.city,
                            state: customer.state,
                            country: customer.country,
                            zip: customer.zip,
                            notes: customer.notes || '',
                          });
                          setIsEditDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCustomer(customer.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Customer Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Total Invoices Card */}
                <Card className="p-4 border-blue-100 bg-blue-50">
                  <div className="flex flex-col items-center text-center">
                    <FileText className="h-8 w-8 text-blue-500 mb-2" />
                    <h3 className="text-sm font-medium text-gray-500">Total Invoices</h3>
                    <p className="text-2xl font-bold mt-1">{customerInvoices?.length || 0}</p>
                    <p className="text-sm font-medium text-gray-700 mt-1">
                      Total Amount
                    </p>
                    <p className="text-xl font-bold text-blue-600">
                      {formatCurrency(customerInvoices?.reduce((sum, inv) => sum + (inv.total || 0), 0) || 0)}
                    </p>
                  </div>
                </Card>

                {/* Current Card */}
                <Card className="p-4 border-green-100 bg-green-50">
                  <div className="flex flex-col items-center text-center">
                    <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
                    <h3 className="text-sm font-medium text-gray-500">Current</h3>
                    <p className="text-2xl font-bold mt-1">
                      {customerInvoices?.filter(inv => inv.status === 'paid').length || 0}
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-1">
                      Paid Amount
                    </p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(customerInvoices?.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.total || 0), 0) || 0)}
                    </p>
                  </div>
                </Card>

                {/* Past-due Card */}
                <Card className="p-4 border-red-100 bg-red-50">
                  <div className="flex flex-col items-center text-center">
                    <AlertTriangle className="h-8 w-8 text-red-500 mb-2" />
                    <h3 className="text-sm font-medium text-gray-500">Past-due</h3>
                    <p className="text-2xl font-bold mt-1">
                      {customerInvoices?.filter(inv => inv.status === 'overdue').length || 0}
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-1">
                      Past-due Amount
                    </p>
                    <p className="text-xl font-bold text-red-600">
                      {formatCurrency(customerInvoices?.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + (inv.total || 0), 0) || 0)}
                    </p>
                  </div>
                </Card>

                {/* Aging Breakdown Card */}
                <Card className="p-4 border-purple-100 bg-purple-50">
                  <div className="flex flex-col items-center text-center">
                    <Clock className="h-8 w-8 text-purple-500 mb-2" />
                    <h3 className="text-sm font-medium text-gray-500">Aging</h3>
                    <div className="w-full mt-2 space-y-2 text-left">
                      <div className="flex justify-between text-xs">
                        <span>0-30:</span>
                        <span className="font-semibold">{formatCurrency(getAgingAmounts()['0-30'])}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>30-60:</span>
                        <span className="font-semibold">{formatCurrency(getAgingAmounts()['30-60'])}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>60-90:</span>
                        <span className="font-semibold">{formatCurrency(getAgingAmounts()['60-90'])}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>90+:</span>
                        <span className="font-semibold">{formatCurrency(getAgingAmounts()['90+'])}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Customer Information Accordion */}
              <div className="border rounded-md">
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() => setIsCustomerInfoExpanded(!isCustomerInfoExpanded)}
                >
                  <h3 className="text-lg font-medium flex items-center">
                    <Users className="mr-2 h-5 w-5" />
                    Customer Information
                  </h3>
                  <ChevronDown 
                    className={`h-5 w-5 transition-transform ${isCustomerInfoExpanded ? 'transform rotate-180' : ''}`}
                  />
                </div>
                {isCustomerInfoExpanded && (
                  <div className="px-4 pb-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1">Company</h4>
                        <p className="font-medium">{selectedCustomer.companyName || 'Testing Company'}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center">
                          <Phone className="h-4 w-4 mr-1" /> Phone
                        </h4>
                        <p className="font-medium">{selectedCustomer.phone || '+1 (305) 205-9132'}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center">
                          <MapPin className="h-4 w-4 mr-1" /> Address
                        </h4>
                        <p className="font-medium">
                          {selectedCustomer.street}, {selectedCustomer.city}, {selectedCustomer.state} {selectedCustomer.zip}
                        </p>
                        <p>{selectedCustomer.country}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center">
                          <Mail className="h-4 w-4 mr-1" /> Email
                        </h4>
                        <p className="font-medium">{selectedCustomer.email}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center">
                          <Calendar className="h-4 w-4 mr-1" /> Customer Since
                        </h4>
                        <p className="font-medium">{formatDate(selectedCustomer.createdAt || new Date())}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Invoice History Accordion */}
              <div className="border rounded-md">
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() => setIsInvoiceHistoryExpanded(!isInvoiceHistoryExpanded)}
                >
                  <h3 className="text-lg font-medium flex items-center">
                    <Receipt className="mr-2 h-5 w-5" />
                    Invoice History
                    <span className="ml-2 text-sm text-gray-500">
                      Showing {customerInvoices?.length || 0} of {customerInvoices?.length || 0} invoices
                    </span>
                  </h3>
                  <ChevronDown 
                    className={`h-5 w-5 transition-transform ${isInvoiceHistoryExpanded ? 'transform rotate-180' : ''}`}
                  />
                </div>
                {isInvoiceHistoryExpanded && (
                  <div className="px-4 pb-4">
                    <InvoiceList 
                      invoices={getFilteredInvoices()} 
                      emptyMessage="No invoices found for this customer." 
                      isLoading={isLoadingCustomerInvoices}
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setIsDetailsDialogOpen(false)}
                >
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    setIsDetailsDialogOpen(false);
                    navigate(`/app/invoices/new?customerId=${selectedCustomer.id}`);
                  }}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Create Invoice
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Add New Customer
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <CustomerFormFields
              formData={formData}
              onChange={handleFieldChange}
              onAddressChange={handleAddressChange}
              showCruiseFields={companySettingsQuery.data?.invoiceSettings?.industry === 'cruise_ship'}
            />

            <DialogFooter className="pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createCustomerMutation.isPending}
                className="gap-2"
              >
                {createCustomerMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Save Customer
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-6">
            <CustomerFormFields
              formData={formData}
              onChange={handleFieldChange}
              onAddressChange={handleAddressChange}
              showCruiseFields={companySettingsQuery.data?.invoiceSettings?.industry === 'cruise_ship'}
            />

            <DialogFooter className="pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateCustomerMutation.isPending}>
                {updateCustomerMutation.isPending ? 'Updating...' : 'Update Customer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Customer
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this customer? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 p-4 rounded-lg border border-red-100 mb-4">
            <p className="text-sm text-red-600">
              Warning: Deleting a customer will also remove their associated invoices and payment information.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={deleteCustomerMutation.isPending}
            >
              {deleteCustomerMutation.isPending ? 'Deleting...' : 'Delete Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Customer Invoices Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}'s Invoices` : 'Customer Invoices'}
            </DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="p-4 bg-blue-50 border-blue-100">
                  <div className="flex flex-col items-center">
                    <FileText className="h-8 w-8 text-blue-500 mb-2" />
                    <h3 className="text-lg font-medium">Total</h3>
                    <p className="text-3xl font-bold">{metrics?.totalInvoices || 0}</p>
                  </div>
                </Card>
                
                <Card className="p-4 bg-green-50 border-green-100">
                  <div className="flex flex-col items-center">
                    <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
                    <h3 className="text-lg font-medium">Paid</h3>
                    <p className="text-3xl font-bold">{metrics?.paidInvoices || 0}</p>
                  </div>
                </Card>
                
                <Card className="p-4 bg-amber-50 border-amber-100">
                  <div className="flex flex-col items-center">
                    <AlertCircle className="h-8 w-8 text-amber-500 mb-2" />
                    <h3 className="text-lg font-medium">Pending</h3>
                    <p className="text-3xl font-bold">{metrics?.pendingInvoices || 0}</p>
                  </div>
                </Card>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Invoice History</h3>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/app/invoices/new?customerId=${selectedCustomer.id}`)}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Invoice
                  </Button>
                </div>
                
                <InvoiceList
                  invoices={customerInvoices}
                  emptyMessage="No invoices found for this customer."
                  isLoading={isLoadingCustomerInvoices}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        open={isCustomerProfileModalOpen}
        onOpenChange={setIsCustomerProfileModalOpen}
        customer={selectedCustomer}
      />
    </div>
  );
}

// Update the InvoiceList component to handle loading state
interface InvoiceListProps {
  invoices: any[];
  emptyMessage: string;
  isLoading?: boolean;
}

const InvoiceList = ({ invoices, emptyMessage, isLoading }: InvoiceListProps) => {
  if (isLoading) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 rounded-full bg-gray-200 mb-2"></div>
          <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
          <div className="h-3 w-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!invoices || !invoices.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="h-12 w-12 mx-auto text-gray-300 mb-2" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  console.log('Rendering invoice list with', invoices.length, 'invoices');

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-xs text-gray-500 uppercase border-b">
              <th className="px-4 py-3 text-left">Invoice #</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="border-b hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 font-medium">#{invoice.invoiceNumber || invoice.id.slice(0, 8)}</td>
                <td className="px-4 py-3 text-sm">{formatDate(invoice.date || invoice.createdAt)}</td>
                <td className="px-4 py-3">
                  <Badge className={getInvoiceStatusBadgeClass(invoice.status || 'draft')}>
                    {(invoice.status || 'draft').charAt(0).toUpperCase() + (invoice.status || 'draft').slice(1)}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right font-medium">{formatCurrency(invoice.total || 0)}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" className="text-xs">
                    <ArrowRight className="h-3 w-3 mr-1" /> View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
